import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  AlertTriangle,
  Shield,
  Users,
  Search,
  Check,
  X,
  RefreshCw,
  Loader2,
  AlertCircle,
  Activity,
  Server,
  Database,
  Lock,
  Eye,
  Clock,
  ChevronRight,
  Mail,
  Wifi,
  WifiOff,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SiteReportedIssuesManager = () => {
  const [activeTab, setActiveTab] = useState('warnings');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data states
  const [warnings, setWarnings] = useState([]);
  const [warningStats, setWarningStats] = useState({ total: 0, active: 0, critical: 0 });
  const [loginIssues, setLoginIssues] = useState([]);
  const [loginStats, setLoginStats] = useState({ total: 0, today: 0 });
  const [healthStatus, setHealthStatus] = useState(null);
  const [healthEvents, setHealthEvents] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch warnings
      const warningsRes = await axios.get(`${API}/monitoring/warnings`, { headers });
      setWarnings(warningsRes.data.warnings || []);
      setWarningStats(warningsRes.data.stats || { total: 0, active: 0, critical: 0 });

      // Fetch login issues
      const loginRes = await axios.get(`${API}/monitoring/login-issues`, { headers });
      setLoginIssues(loginRes.data.issues || []);
      setLoginStats(loginRes.data.stats || { total: 0, today: 0 });

      // Fetch health status
      const healthRes = await axios.get(`${API}/monitoring/health`, { headers });
      setHealthStatus(healthRes.data.current_status);
      setHealthEvents(healthRes.data.recent_events || []);

    } catch (err) {
      console.error('Error fetching monitoring data:', err);
      toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleRunHealthCheck = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/monitoring/health/check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Health check completed');
      await fetchAllData();
    } catch (err) {
      toast.error('Health check failed');
    }
  };

  const handleAcknowledgeWarning = async (warningId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/monitoring/warnings/${warningId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Warning acknowledged');
      await fetchAllData();
    } catch (err) {
      toast.error('Failed to acknowledge warning');
    }
  };

  const handleDismissWarning = async (warningId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/monitoring/warnings/${warningId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Warning dismissed');
      await fetchAllData();
    } catch (err) {
      toast.error('Failed to dismiss warning');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'acknowledged': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'dismissed': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  // Filter warnings based on search
  const filteredWarnings = warnings.filter(w => 
    w.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.details?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#6D4AFF]" />
      </div>
    );
  }

  return (
    <Card className="bg-[#1a1a2e] border-[#2d2d44]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Site Reported Issues
            </CardTitle>
            <CardDescription className="text-gray-400">
              AI-powered monitoring for spam, security, and system health
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-[#2d2d44] text-gray-400 hover:bg-[#252540]"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#252540] border border-[#2d2d44] p-1 mb-6">
            <TabsTrigger 
              value="warnings" 
              className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white"
            >
              <Shield className="w-4 h-4 mr-2" />
              Warned Users
              {warningStats.active > 0 && (
                <Badge className="ml-2 bg-red-500/20 text-red-400">{warningStats.active}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="login" 
              className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white"
            >
              <Lock className="w-4 h-4 mr-2" />
              Login Issues
              {loginStats.today > 0 && (
                <Badge className="ml-2 bg-yellow-500/20 text-yellow-400">{loginStats.today}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="health" 
              className="data-[state=active]:bg-[#6D4AFF] data-[state=active]:text-white"
            >
              <Activity className="w-4 h-4 mr-2" />
              System Health
            </TabsTrigger>
          </TabsList>

          {/* Warned Users Tab */}
          <TabsContent value="warnings">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-[#252540] rounded-xl p-4 border border-[#2d2d44]">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-400">Total Warnings</span>
                </div>
                <p className="text-2xl font-bold text-white">{warningStats.total}</p>
              </div>
              <div className="bg-[#252540] rounded-xl p-4 border border-[#2d2d44]">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-gray-400">Active</span>
                </div>
                <p className="text-2xl font-bold text-amber-400">{warningStats.active}</p>
              </div>
              <div className="bg-[#252540] rounded-xl p-4 border border-[#2d2d44]">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-gray-400">Critical</span>
                </div>
                <p className="text-2xl font-bold text-red-400">{warningStats.critical}</p>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search warnings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#252540] border-[#2d2d44] text-white"
              />
            </div>

            {/* Warnings List */}
            <div className="space-y-3">
              {filteredWarnings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No warnings found</p>
                </div>
              ) : (
                filteredWarnings.map((warning) => (
                  <div
                    key={warning.id}
                    className="bg-[#252540] border border-[#2d2d44] rounded-xl p-4 hover:border-[#3d3d54] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getSeverityColor(warning.severity)}>
                            {warning.severity}
                          </Badge>
                          <Badge className={getStatusColor(warning.status)}>
                            {warning.status}
                          </Badge>
                          {warning.auto_action && (
                            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                              {warning.auto_action}
                            </Badge>
                          )}
                        </div>
                        <p className="text-white font-medium">{warning.user_email}</p>
                        <p className="text-sm text-gray-400 mt-1">{warning.details}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(warning.created_at)}
                          </span>
                          <span>Type: {warning.warning_type}</span>
                        </div>
                      </div>
                      
                      {warning.status === 'active' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAcknowledgeWarning(warning.id)}
                            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDismissWarning(warning.id)}
                            className="border-gray-500/50 text-gray-400 hover:bg-gray-500/10"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Login Issues Tab */}
          <TabsContent value="login">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-[#252540] rounded-xl p-4 border border-[#2d2d44]">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-400">Total Issues</span>
                </div>
                <p className="text-2xl font-bold text-white">{loginStats.total}</p>
              </div>
              <div className="bg-[#252540] rounded-xl p-4 border border-[#2d2d44]">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-gray-400">Today</span>
                </div>
                <p className="text-2xl font-bold text-amber-400">{loginStats.today}</p>
              </div>
            </div>

            {/* Login Issues List */}
            <div className="space-y-3">
              {loginIssues.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No login issues recorded</p>
                </div>
              ) : (
                loginIssues.map((issue) => (
                  <div
                    key={issue.id}
                    className="bg-[#252540] border border-[#2d2d44] rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={
                            issue.issue_type === 'failed_attempt' 
                              ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                              : issue.issue_type === 'account_locked'
                              ? 'bg-red-500/20 text-red-400 border-red-500/30'
                              : 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                          }>
                            {issue.issue_type?.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-white font-medium">{issue.email}</p>
                        <p className="text-sm text-gray-400 mt-1">{issue.details}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(issue.created_at)}
                          </span>
                          {issue.ip_address && (
                            <span>IP: {issue.ip_address}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* System Health Tab */}
          <TabsContent value="health">
            <div className="space-y-6">
              {/* Current Status */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium">Current Status</h3>
                  <Button
                    size="sm"
                    onClick={handleRunHealthCheck}
                    className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                  >
                    <Activity className="w-4 h-4 mr-2" />
                    Run Health Check
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Database Status */}
                  <div className={`p-4 rounded-xl border ${
                    healthStatus?.database?.status === 'healthy'
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    <div className="flex items-center gap-3">
                      <Database className={`w-6 h-6 ${
                        healthStatus?.database?.status === 'healthy' ? 'text-green-400' : 'text-red-400'
                      }`} />
                      <div>
                        <p className="text-white font-medium">Database</p>
                        <p className={`text-sm ${
                          healthStatus?.database?.status === 'healthy' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {healthStatus?.database?.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
                        </p>
                      </div>
                      {healthStatus?.database?.status === 'healthy' ? (
                        <Wifi className="w-5 h-5 text-green-400 ml-auto" />
                      ) : (
                        <WifiOff className="w-5 h-5 text-red-400 ml-auto" />
                      )}
                    </div>
                  </div>

                  {/* API Status */}
                  <div className="p-4 rounded-xl border bg-green-500/10 border-green-500/30">
                    <div className="flex items-center gap-3">
                      <Server className="w-6 h-6 text-green-400" />
                      <div>
                        <p className="text-white font-medium">API Server</p>
                        <p className="text-sm text-green-400">Online</p>
                      </div>
                      <Wifi className="w-5 h-5 text-green-400 ml-auto" />
                    </div>
                  </div>

                  {/* Email Status */}
                  <div className="p-4 rounded-xl border bg-green-500/10 border-green-500/30">
                    <div className="flex items-center gap-3">
                      <Mail className="w-6 h-6 text-green-400" />
                      <div>
                        <p className="text-white font-medium">Email Service</p>
                        <p className="text-sm text-green-400">Operational</p>
                      </div>
                      <Wifi className="w-5 h-5 text-green-400 ml-auto" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Events */}
              <div>
                <h3 className="text-white font-medium mb-4">Recent Health Events</h3>
                <div className="space-y-2">
                  {healthEvents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Activity className="w-10 h-10 mx-auto mb-3 opacity-50" />
                      <p>No health events recorded yet</p>
                    </div>
                  ) : (
                    healthEvents.slice(0, 10).map((event, index) => (
                      <div
                        key={event.id || index}
                        className="flex items-center gap-3 p-3 bg-[#252540] rounded-lg border border-[#2d2d44]"
                      >
                        {event.status === 'healthy' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                        <span className="text-white">{event.component}</span>
                        <Badge className={
                          event.status === 'healthy'
                            ? 'bg-green-500/20 text-green-400 border-green-500/30'
                            : 'bg-red-500/20 text-red-400 border-red-500/30'
                        }>
                          {event.status}
                        </Badge>
                        <span className="text-xs text-gray-500 ml-auto">
                          {formatDate(event.created_at)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Alert Info */}
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                  <div>
                    <p className="text-amber-400 font-medium">Server Down Alerts</p>
                    <p className="text-sm text-gray-400 mt-1">
                      If any critical component goes down, an alert email will be immediately sent to{' '}
                      <span className="text-white">mel@a2gdesigns.com</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SiteReportedIssuesManager;
